import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [previewPartner, setPreviewPartner] = useState(() => {
    try {
      // 1) Read from URL first (highest priority)
      const params = new URLSearchParams(window.location.search);
      const pid = params.get('preview_as_partner');
      if (pid) {
        // Persist as placeholder; permissions filled by useEffect after partners list loads
        const placeholder = { id: pid, name: 'Parceiro', permissions: {}, _pending: true };
        sessionStorage.setItem('previewPartner', JSON.stringify(placeholder));
        return placeholder;
      }
      // 2) Restore from sessionStorage if user already entered preview mode
      const raw = sessionStorage.getItem('previewPartner');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Sessão expirada: adota token mais recente de outro separador ou força novo login.
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, async (error) => {
      const cfg = error?.config || {};
      const url = cfg.url || '';
      if (error?.response?.status !== 401 || /\/auth\/(login|register)|\/public\//.test(url)) throw error;
      const sent = (cfg.headers?.Authorization || cfg.headers?.authorization || '').replace('Bearer ', '');
      const latest = localStorage.getItem('token');
      if (latest && sent && latest !== sent && !cfg._retried) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${latest}`;
        cfg.headers = { ...cfg.headers, Authorization: `Bearer ${latest}` };
        cfg._retried = true;
        return axios(cfg);
      }
      if (sent && window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        sessionStorage.setItem('sessionExpired', '1');
        window.location.assign('/login');
      }
      throw error;
    });
    return () => axios.interceptors.response.eject(id);
  }, []);


  // Resolve partner name + permissions when preview_as_partner is set (pending)
  useEffect(() => {
    if (!user) return;
    if (!previewPartner?._pending) return;
    if (!['super_admin', 'gestor'].includes(user.role)) {
      // Non-privileged user: clear preview
      setPreviewPartner(null);
      sessionStorage.removeItem('previewPartner');
      return;
    }
    let cancelled = false;
    axios.get(`${API_URL}/partners`)
      .then(res => {
        if (cancelled) return;
        const partner = (res.data || []).find(p => p.id === previewPartner.id);
        if (!partner) {
          setPreviewPartner(null);
          sessionStorage.removeItem('previewPartner');
          return;
        }
        const data = { id: partner.id, name: partner.name, permissions: partner.permissions || {} };
        setPreviewPartner(data);
        sessionStorage.setItem('previewPartner', JSON.stringify(data));
      })
      .catch(() => { /* keep _pending so we can retry on next load */ });
    return () => { cancelled = true; };
  }, [user, previewPartner?._pending, previewPartner?.id]);

  const exitPreview = () => {
    setPreviewPartner(null);
    sessionStorage.removeItem('previewPartner');
    // Strip ?preview_as_partner from URL
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('preview_as_partner');
      window.history.replaceState({}, '', url.toString());
    } catch {}
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    // If must change password, don't set user - return data for Login to handle
    if (userData.must_change_password) {
      return { user: userData, access_token, mustChangePassword: true };
    }
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return { user: userData, access_token };
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => reg?.pushManager.getSubscription()).then((sub) => {
        if (sub) axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/push/unsubscribe`, { endpoint: sub.endpoint }).catch(() => {});
      }).catch(() => {});
    }
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isGestor = () => user?.role === 'gestor';
  const isAdmin = () => ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const isFullAccess = () => ['super_admin', 'gestor'].includes(user?.role);
  const isManager = () => ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice'].includes(user?.role);
  const isBackoffice = () => user?.role === 'backoffice';
  const isComercial = () => user?.role === 'comercial';
  const isAngariador = () => user?.role === 'angariador';
  const isChefeEquipa = () => user?.role === 'chefe_equipa';

  // Check permission - cascade: user_permissions > partner_permissions > role default
  const hasPerm = (perm) => {
    // Preview mode: super_admin/gestor temporarily acts as if they were a partner admin.
    // The override applies to UI-level checks only (sidebar / hidden buttons).
    if (previewPartner?.permissions) {
      const pp = previewPartner.permissions;
      if (pp[perm] !== undefined) return pp[perm] !== false;
      return true;
    }
    if (['super_admin', 'gestor'].includes(user?.role)) return true;
    // Check user-level permission first (most specific)
    if (user?.user_permissions && user.user_permissions[perm] !== undefined) {
      return user.user_permissions[perm] !== false;
    }
    // Then partner-level (only for admin/parceiro)
    if (['admin', 'parceiro'].includes(user?.role) && user?.partner_permissions) {
      if (user.partner_permissions[perm] !== undefined) {
        return user.partner_permissions[perm] !== false;
      }
    }
    // Default: allowed
    return true;
  };

  // Check if a sidebar section is visible for this user (based on partner sidebar settings).
  // Applies to ALL users of a partner (not only admin/parceiro).
  // `defaultValue` is used when the key is not set on partner_permissions.
  const canSeeSidebar = (key, defaultValue = true) => {
    // Preview mode: use the previewed partner's sidebar settings
    if (previewPartner?.permissions) {
      const sb = previewPartner.permissions;
      const sidebarKey = `sidebar_${key}`;
      if (sb[sidebarKey] === false) return false;
      if (sb[sidebarKey] === true) return true;
      return defaultValue;
    }
    if (['super_admin', 'gestor'].includes(user?.role)) return true;
    const sb = user?.partner_permissions || {};
    const sidebarKey = `sidebar_${key}`;
    if (sb[sidebarKey] === false) return false;
    if (sb[sidebarKey] === true) return true;
    return defaultValue;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser: fetchUser,
      isSuperAdmin,
      isGestor,
      isAdmin,
      isFullAccess,
      isManager,
      isBackoffice,
      isComercial,
      isAngariador,
      isChefeEquipa,
      hasPerm,
      canSeeSidebar,
      previewPartner,
      exitPreview
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
